import type { FileDiff, Message } from "@mux/sdk/v2"
import type { ServerConnection } from "@/context/server"
import { jsonArray, jsonNumber, jsonObject, jsonString } from "@/transport/decode"
import { expectJson } from "@/transport/http"
import { serverFetch } from "@/transport/server"

export type VersionedTurnDiff = { revision: number; summary: TurnDiffSummary }
export type TurnDiffMessage = Message & { turnDiff?: VersionedTurnDiff }

export function messageTurnDiff(message: Message | undefined): VersionedTurnDiff | undefined {
  return (message as TurnDiffMessage | undefined)?.turnDiff
}

export type TurnDiffSnapshot = {
  revision: number
  summaries: Record<string, TurnDiffSummary>
}

export type TurnDiffSummary = {
  files: number
  additions: number
  deletions: number
}

function decodeSummary(value: unknown): TurnDiffSummary {
  const summary = jsonObject(value, "Server returned an invalid turn diff summary")
  return {
    files: jsonNumber(summary.files, "Server returned an invalid turn diff summary"),
    additions: jsonNumber(summary.additions, "Server returned an invalid turn diff summary"),
    deletions: jsonNumber(summary.deletions, "Server returned an invalid turn diff summary"),
  }
}

function decodeSummaries(value: unknown) {
  const record = jsonObject(value, "Server returned invalid turn diff summaries")
  return Object.fromEntries(Object.entries(record).map(([messageID, summary]) => [messageID, decodeSummary(summary)]))
}

function decodeTurnDiffSnapshot(value: unknown): TurnDiffSnapshot {
  const object = jsonObject(value, "Server returned invalid turn diff summaries")
  return { revision: jsonNumber(object.revision, "Invalid turn diff revision"), summaries: decodeSummaries(object.summaries) }
}

function decodeDiff(value: unknown): FileDiff {
  const diff = jsonObject(value, "Server returned an invalid turn diff")
  const status = jsonString(diff.status, "Server returned an invalid turn diff")
  if (status !== "added" && status !== "deleted" && status !== "modified") {
    throw new Error("Server returned an invalid turn diff")
  }
  return {
    file: jsonString(diff.file, "Server returned an invalid turn diff"),
    before: jsonString(diff.before, "Server returned an invalid turn diff"),
    after: jsonString(diff.after, "Server returned an invalid turn diff"),
    additions: jsonNumber(diff.additions, "Server returned an invalid turn diff"),
    deletions: jsonNumber(diff.deletions, "Server returned an invalid turn diff"),
    status,
  }
}

const TURN_DIFF_SUMMARY_TIMEOUT_MS = 3_000

export function loadTurnDiffSummaries(
  server: ServerConnection.HttpBase,
  sessionID: string,
  options?: { fetch?: typeof globalThis.fetch; timeoutMs?: number; signal?: AbortSignal },
) {
  const path = `/session/${encodeURIComponent(sessionID)}/turn-diffs`
  const controller = new AbortController()
  const timeout = setTimeout(
    () => controller.abort(),
    options?.timeoutMs ?? TURN_DIFF_SUMMARY_TIMEOUT_MS,
  )
  const signal = options?.signal ? AbortSignal.any([controller.signal, options.signal]) : controller.signal
  return serverFetch(server, path, { signal }, { fetch: options?.fetch })
    .then((response) =>
      expectJson(response, decodeTurnDiffSnapshot, { error: "Unable to load turn diff summaries" }),
    )
    .finally(() => clearTimeout(timeout))
}

export function loadTurnDiff(
  server: ServerConnection.HttpBase,
  sessionID: string,
  messageID: string,
  customFetch?: typeof globalThis.fetch,
  signal?: AbortSignal,
) {
  const path = `/session/${encodeURIComponent(sessionID)}/turn/${encodeURIComponent(messageID)}/diff`
  return serverFetch(server, path, { signal }, { fetch: customFetch }).then((response) =>
    expectJson(response, (value) => jsonArray(value, "Server returned invalid turn diffs").map(decodeDiff), {
      error: "Unable to load turn diff",
    }),
  )
}

/** One kind of file in a diff, such as Runtime or Tests, and the lines its files changed. */
export type DiffGroup = {
  name: string
  files: number
  additions: number
  deletions: number
  /** The group's files, named as the diff names them. */
  paths: string[]
}

function decodeDiffGroups(value: unknown): DiffGroup[] {
  const error = "Server returned invalid diff groups"
  const body = jsonObject(value, error)
  return jsonArray(body.groups, error).map((item) => {
    const group = jsonObject(item, error)
    return {
      name: jsonString(group.name, error),
      files: jsonNumber(group.files, error),
      additions: jsonNumber(group.additions, error),
      deletions: jsonNumber(group.deletions, error),
      paths: jsonArray(group.paths, error).map((path) => jsonString(path, error)),
    }
  })
}

/**
 * Splits a turn's changes, or without a messageID the session's changes, by
 * kind of file. An empty list means there is nothing to split.
 */
export function loadDiffGroups(
  server: ServerConnection.HttpBase,
  sessionID: string,
  messageID: string | undefined,
  customFetch?: typeof globalThis.fetch,
  signal?: AbortSignal,
) {
  const session = `/session/${encodeURIComponent(sessionID)}`
  const path = messageID ? `${session}/turn/${encodeURIComponent(messageID)}/diff-groups` : `${session}/diff-groups`
  return serverFetch(server, path, { signal }, { fetch: customFetch }).then((response) =>
    expectJson(response, decodeDiffGroups, { error: "Unable to group changes" }),
  )
}
